import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, LockKey, ShieldCheck, Eye, EyeSlash } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const tooShort = password.length > 0 && password.length < 6;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = !!token && password.length >= 6 && password === confirm && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (data?.user) setUser(data.user);
      const u = data?.user;
      // Show the user their username with the success toast so they don't forget it again.
      if (u?.username) {
        toast.success(`Password reset — signed in as @${u.username}`, { duration: 6000 });
      } else {
        toast.success("Password reset — you're signed in.");
      }
      if (!u?.username) navigate("/choose-username", { replace: true });
      else if (!u.onboarded) navigate("/onboarding", { replace: true });
      else if (!u.verified) navigate("/verify", { replace: true });
      else navigate("/feed", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 410) toast.error(detail || "This link has expired. Request a new one.");
      else if (status === 400) toast.error(detail || "This link is invalid.");
      else if (status === 429) toast.error("Too many attempts. Wait a few minutes.");
      else toast.error(detail || "Couldn't reset. Try again.");
    } finally { setSubmitting(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6" data-testid="reset-no-token">
        <div className="max-w-sm text-center">
          <Logo size={40} />
          <h1 className="mt-6 font-heading text-3xl font-black">Bad link</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            This reset link is missing its token. Tap your latest reset email again, or request a fresh one.
          </p>
          <Link to="/forgot-password" className="mt-6 inline-block px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold">
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="reset-password-page">
      <div className="mx-auto max-w-md min-h-screen sm:border-x sm:border-white/5 px-6 py-10 flex flex-col">
        <div className="flex items-center justify-between mb-8">
          <Logo size={36} />
          <Link to="/" data-testid="reset-back" className="p-2 text-muted-foreground hover:text-foreground rounded-full">
            <ArrowLeft size={18} />
          </Link>
        </div>

        <h1 className="font-heading text-4xl font-black tracking-tight">
          Pick a new<br /><span className="text-primary">password.</span>
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Use at least 6 characters. Something you'll actually remember — write it down if you must.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-3">
          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">New password</span>
            <div className="mt-1.5 relative">
              <LockKey size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="reset-password-input"
                type={show ? "text" : "password"}
                autoFocus
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                minLength={6}
                maxLength={128}
                required
                className="w-full bg-secondary border border-white/10 rounded-2xl pl-10 pr-12 py-3.5 text-base focus:outline-none focus:border-primary"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                data-testid="reset-password-show"
                aria-label={show ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground"
              >
                {show ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {tooShort && <p className="mt-1.5 text-[11px] text-destructive">Needs at least 6 characters.</p>}
          </label>

          <label className="block">
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Confirm</span>
            <div className="mt-1.5 relative">
              <LockKey size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="reset-confirm-input"
                type={show ? "text" : "password"}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Type it again"
                minLength={6}
                maxLength={128}
                required
                className="w-full bg-secondary border border-white/10 rounded-2xl pl-10 pr-3 py-3.5 text-base focus:outline-none focus:border-primary"
              />
            </div>
            {mismatch && <p className="mt-1.5 text-[11px] text-destructive">Passwords don't match.</p>}
          </label>

          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="reset-submit"
            className="mt-2 w-full rounded-full bg-primary text-primary-foreground py-4 text-base font-semibold flex items-center justify-center gap-2 glow-hover disabled:opacity-40"
          >
            <ShieldCheck size={18} weight="fill" />
            {submitting ? "Resetting…" : "Reset & sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
